
import React, { useState } from 'react';
import { format } from 'date-fns';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  ChevronDown, 
  Mail, 
  User,
  MoreVertical,
  MessageSquare,
  ExternalLink,
  Send,
  DollarSign,
  Package,
  Phone
} from 'lucide-react';
import { ProjectOrder } from '@/hooks/use-admin-store';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';

interface ProjectsListProps {
  projects: ProjectOrder[];
  onUpdateStatus: (id: string, status: string) => Promise<void>;
}

const ProjectsList: React.FC<ProjectsListProps> = ({ projects, onUpdateStatus }) => {
  const [selectedProject, setSelectedProject] = useState<ProjectOrder | null>(null);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleStatusChange = async (id: string, status: string) => {
    await onUpdateStatus(id, status);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>;
      case 'paid':
        return <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 hover:bg-blue-100">Processing</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-100 text-red-800 hover:bg-red-100">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleSendMessage = () => {
    if (!selectedProject || !message.trim()) return;
    
    setIsSending(true);
    
    // Simulate sending email
    setTimeout(() => {
      setIsSending(false);
      setMessage('');
      toast({
        title: "Message Sent",
        description: `Email sent to ${selectedProject.customerEmail}`,
      });
    }, 1000);
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Customer</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No projects found.
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{project.customerName}</span>
                      <span className="text-xs text-muted-foreground">{project.customerEmail}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="secondary" className="w-fit">
                        {project.serviceType}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {project.serviceTier}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 mr-1" />
                      {project.price.toFixed(2)}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(project.paymentStatus)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSelectedProject(project)}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>Project Details</DialogTitle>
                          </DialogHeader>
                          {selectedProject && (
                            <div className="space-y-4 pt-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <User className="h-4 w-4 mr-2" /> Customer
                                  </h3>
                                  <p>{selectedProject.customerName}</p>
                                  <p className="text-sm text-muted-foreground">{selectedProject.customerEmail}</p>
                                </div>
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <Phone className="h-4 w-4 mr-2" /> Phone
                                  </h3>
                                  <p>{selectedProject.customerPhone}</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <Package className="h-4 w-4 mr-2" /> Service
                                  </h3>
                                  <div className="flex flex-col gap-1 mt-1">
                                    <Badge variant="secondary" className="w-fit">
                                      {selectedProject.serviceType}
                                    </Badge>
                                    <span className="text-sm">
                                      {selectedProject.serviceTier}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <DollarSign className="h-4 w-4 mr-2" /> Price
                                  </h3>
                                  <p className="text-xl font-medium">${selectedProject.price.toFixed(2)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Payment via {selectedProject.paymentProvider}
                                  </p>
                                </div>
                              </div>
                              
                              {selectedProject.projectDescription && (
                                <div>
                                  <h3 className="font-semibold">Project Description</h3>
                                  <p className="p-3 rounded-md bg-muted/50 mt-1">
                                    {selectedProject.projectDescription}
                                  </p>
                                </div>
                              )}
                              
                              <div>
                                <h3 className="font-semibold">Status</h3>
                                <div className="flex gap-2 mt-1">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" className="w-full">
                                        {getStatusBadge(selectedProject.paymentStatus)}
                                        <ChevronDown className="ml-2 h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedProject.id, 'pending')}>
                                        Pending
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedProject.id, 'paid')}>
                                        Paid
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedProject.id, 'processing')}>
                                        Processing
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedProject.id, 'completed')}>
                                        Completed
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedProject.id, 'cancelled')}>
                                        Cancelled
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                              
                              <div className="border-t pt-4 mt-4">
                                <h3 className="font-semibold flex items-center">
                                  <Mail className="h-4 w-4 mr-2" /> Send Message
                                </h3>
                                <Textarea
                                  placeholder="Type your message here..."
                                  className="mt-2"
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                />
                                <Button 
                                  className="mt-2 w-full"
                                  disabled={!message.trim() || isSending}
                                  onClick={handleSendMessage}
                                >
                                  {isSending ? (
                                    <>Sending...</>
                                  ) : (
                                    <>
                                      <Send className="mr-2 h-4 w-4" />
                                      Send Email
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'paid')}>
                            Mark as Paid
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'processing')}>
                            Mark as Processing
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'completed')}>
                            Mark as Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'cancelled')}>
                            Mark as Cancelled
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => window.open(`mailto:${project.customerEmail}`, '_blank')}
                            className="flex items-center"
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Email Customer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default ProjectsList;
